import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {BankStatement, Transaction, TransactionStatement} from '../bank-statement.model';
import {MdDialogRef} from '@angular/material';
import {FormBuilder, FormGroup, FormArray, Validators} from '@angular/forms';
import {BankStatementController} from '../bank-statement/bank-statement.controller';

@Component({
  selector: 'app-edit-transaction',
  templateUrl: './edit-transaction.component.html',
  styleUrls: ['./edit-transaction.component.css']
})
export class EditTransactionComponent implements OnInit {

  @ViewChild('transactionsContainer') transactionsContainer: ElementRef;

  bankStatement: BankStatement;
  transactionStatement: TransactionStatement;
  form: FormGroup;
  onReload: (reload: boolean) => void;
  isBalanceValid = true;

  constructor(public dialogRef: MdDialogRef<any>, private fb: FormBuilder, private bankStatementController: BankStatementController) {
    this.form = this.fb.group({
      bankAccount: this.fb.group({
        accountId: [''],
        accountName: [''],
        accountNumber: [''],
        bankInstitutionName: [''],
        currencyValue: ['']
      }),
      documentName: [''],
      jsonData: this.fb.group({
        owner: [''],
        account_number: [''],
        bank_name: [''],
        start_date: [''],
        end_date: [''],
        currency: [''],
        start_balance: [''],
        end_balance: [''],
        transactions: this.fb.array([])
      })
    });
  }

  ngOnInit() {
    this.transactionStatement = this.bankStatement.jsonData;
    this.initForm();
    this.form.valueChanges.subscribe(() => {
      this.validateBalance();
    });
    this.form.get('jsonData.start_date').valueChanges.subscribe(startDate => {
      const endDateControl = this.form.get('jsonData.end_date');
      if (startDate && endDateControl.value && startDate > endDateControl.value) {
        endDateControl.setValue(startDate);
      }
      this.updateTransactionDateLimits();
    });

    this.form.get('jsonData.end_date').valueChanges.subscribe(endDate => {
      const startDateControl = this.form.get('jsonData.start_date');
      if (endDate && startDateControl.value && endDate < startDateControl.value) {
        startDateControl.setValue(endDate);
      }
      this.updateTransactionDateLimits();
    });
  }

  updateTransactionDateLimits() {
    const startDate = this.form.get('jsonData.start_date').value;
    const endDate = this.form.get('jsonData.end_date').value;

    this.transactionsArray.controls.forEach(transaction => {
      const txDateControl = transaction.get('tx_date');
      txDateControl.setValidators([
        Validators.required,
      ]);
      txDateControl.updateValueAndValidity();
    });
  }

  initForm() {
    this.form = this.fb.group({
      jsonData: this.fb.group({
        owner: [this.transactionStatement.owner ? this.transactionStatement.owner : '' ],
        account_number: [this.transactionStatement.account_number ? this.transactionStatement.account_number : ''],
        bank_name: [this.transactionStatement.bank_name ? this.transactionStatement.bank_name : ''],
        start_date: [this.transactionStatement.start_date ? this.transactionStatement.start_date : ''],
        end_date: [this.transactionStatement.end_date ? this.transactionStatement.end_date : ''],
        currency: [this.transactionStatement.currency ? this.transactionStatement.currency : ''],
        start_balance: [this.transactionStatement.start_balance ? this.transactionStatement.start_balance : '0', Validators.pattern(/^\d*\.?\d+$/)],
        end_balance: [this.transactionStatement.end_balance ? this.transactionStatement.end_balance : '0', Validators.pattern(/^\d*\.?\d+$/)],
        transactions: this.fb.array([])
      })
    });
    this.setTransactions();
  }
  setTransactions() {

    const jsonData = this.form.get('jsonData');
    const transactionsArray = jsonData.get('transactions') as FormArray;
    if (this.transactionStatement.transactions) {
      this.transactionStatement.transactions.forEach(tx => {
        transactionsArray.push(this.fb.group({
          tx_date: [tx.tx_date ? tx.tx_date : ''],
          tx_amount: [tx.tx_amount ? tx.tx_amount : '0'],
          tx_description: [tx.tx_description ? tx.tx_description : ''],
          tx_counter_party: [tx.tx_counter_party ? tx.tx_counter_party : ''],
        }));
      });
    }
  }

  addTransaction() {
    const jsonData = this.form.get('jsonData');
    const transactionsArray = jsonData.get('transactions') as FormArray;
    transactionsArray.push(this.fb.group({
      tx_date: [''],
      tx_amount: ['0', [Validators.pattern(/^\d*\.?\d+$/)]],
      tx_description: [''],
      tx_counter_party: ['']
    }));
    setTimeout(() => {
      this.scrollToBottom();
    }, 100);
  }

  removeTransaction(index: number) {
    const jsonData = this.form.get('jsonData');
    const transactionsArray = jsonData.get('transactions') as FormArray;
    transactionsArray.removeAt(index);
  }


  editTransaction(index: number, updatedTransaction: any) {
    const jsonData = this.form.get('jsonData');
    const transactionsArray = jsonData.get('transactions') as FormArray;
    if (transactionsArray.at(index)) {
      transactionsArray.at(index).patchValue(updatedTransaction);
    }
  }
  normalizeTransactionStatement(updatedTransactionStatement: TransactionStatement): TransactionStatement{
    return {
      owner: updatedTransactionStatement.owner,
      account_number: updatedTransactionStatement.account_number,
      bank_name: updatedTransactionStatement.bank_name,
      end_date: updatedTransactionStatement.end_date,
      start_date: updatedTransactionStatement.start_date,
      currency: updatedTransactionStatement.currency,
      start_balance: Number.parseFloat(updatedTransactionStatement.start_balance as string),
      end_balance: Number.parseFloat(updatedTransactionStatement.end_balance as string),
      transactions: updatedTransactionStatement.transactions.map((transaction) => {
        const t: Transaction = {
          tx_amount: Number.parseFloat(transaction.tx_amount as string),
          tx_description: transaction.tx_description,
          tx_counter_party: transaction.tx_counter_party,
          tx_date: transaction.tx_date,
        };
        return t;
      })
    };
  }

  get transactionsArray(): FormArray {
    return this.form.get('jsonData.transactions') as FormArray;
  }

  validateBalance() {
    const jsonData = this.form.get('jsonData').value;
    const endBalance = parseFloat(jsonData.end_balance) || 0;

    const totalTransactions = this.transactionsArray.controls.reduce((sum, control) => {
      return sum + (parseFloat(control.value.tx_amount) || 0);
    }, 0);
    const balanceResult = (totalTransactions) + parseFloat(this.transactionStatement.start_balance as string);
    this.isBalanceValid = endBalance === balanceResult || this.transactionStatement.transactions.length > 0;
  }

  save() {
    const {jsonData} = this.form.value;
    const normalizedJsonData = this.normalizeTransactionStatement(jsonData);
    this.bankStatementController.saveJsonObject(normalizedJsonData, this.bankStatement.id, (success: boolean) => {
      if (success) {
        this.onReload(true);
        this.dialogRef.close(true);
      }
    }, 'Transaction statement updated successfully.', 'Error updating bank statement');
  }
  scrollToBottom() {
    if (this.transactionsContainer) {
      this.transactionsContainer.nativeElement.scrollTop = this.transactionsContainer.nativeElement.scrollHeight;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/finance/edit-transaction/edit-transaction.component.ts